import "../../utils/tailwind.css";
import { PageProvider, useAddWorkProvider } from "./provider";
import Label from "../../components/label";
import Section from "../../components/section";
import { FormikInput } from "../../components/input";
import Button from "../../components/Buttons";
import { Formik, Form, FieldArray } from "formik";
import Switch from "../../components/switch";
import { FormikTextarea } from "../../components/textarea";
import * as Yup from "yup";
import ScrollToFormError from "../../components/scroll-to-form-error";
import { FormikDropdown } from "../../components/dropdown";
import Plus from "../../components/svg/plus";
import Trash from "../../components/svg/trash";
import { dateValidation } from "../../utils/validations";

export default function AddUser() {
  return (
    <div className="p-3">
      <PageProvider>
        <PageContent />
      </PageProvider>
    </div>
  );
}

function PageContent() {
  const { initialValues, handleSubmit, id } = useAddWorkProvider();
  const schemaValidation = Yup.object().shape({
    title: Yup.string().required("This field is required."),
    description: Yup.string().required("This field is required."),
    plan_price: Yup.number()
      .typeError("Plan price must be a number")
      .required("This field is required.")
      .min(0.00, "Plan price must be equal or greater than 0.00")
      .max(99999999.99, "Plan price is too high")
      .test("decimal-precision", "Plan price must have up to 2 decimal places", (value) => /^\d+(\.\d{1,2})?$/.test(value?.toString())),
    currency: Yup.string().required("This field is required."),
    billing_period: Yup.string().required("This field is required."),
    plan_features: Yup.array()
      .of(
        Yup.object().shape({
          feature: Yup.string().required("This field is required."),
        }),
      )
      .min(1, "At least one feature is required")
      .required("This field is required."),
    discount_type: Yup.string().oneOf(["percentage", "fixed"], "Invalid discount type").required("This field is required."),
    start_date: dateValidation(),
    expiry_date: dateValidation(),
  });

  return (
    <Section className="h-[80vh] overflow-y-auto mt-5">
      <Label label={id ? "Update Plan Details" : "Add Plan Details"} className="text-[18px] text-black" />
      <Formik initialValues={initialValues} validationSchema={schemaValidation} enableReinitialize={true} onSubmit={handleSubmit}>
        {({ values, setFieldValue, errors }) => (
          <Form>
            <ScrollToFormError>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div className="flex items-center space-x-2">
                  <Label label="Is Published ?" className="text-black mr-4 mt-3 text-[15px]" />
                  <Switch
                    className="pt-1"
                    type="switch"
                    name="is_publish"
                    onChange={(val) => setFieldValue("is_published", val)}
                    checked={values?.is_published}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div className="flex flex-col">
                  <Label label="Plan Title" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikInput name="title" rootClassName="w-full" />
                </div>
                <div className="flex flex-col ">
                  <Label label="Plan Price" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikInput type="number" name="plan_price" rootClassName="w-full" min={0} />
                </div>
                <div className="flex flex-col ">
                  <Label label="Billing Period" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikDropdown
                    name="billing_period"
                    options={[
                      { label: "Weekly", value: "weekly" },
                      { label: "Monthly", value: "monthly" },
                      { label: "Yearly", value: "yearly" },
                    ]}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col mt-3 ">
                  <Label label="Currency" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikDropdown
                    name="currency"
                    options={[
                      { label: "Indian", value: "indian" },
                      { label: "Dollar", value: "dollar" },
                    ]}
                  />
                </div>
                <div className="flex flex-col mt-3 ">
                  <Label label="Discount Type" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikDropdown
                    name="discount_type"
                    options={[
                      { label: "Percentage", value: "percentage" },
                      { label: "Fixed", value: "fixed" },
                    ]}
                  />
                </div>
                <div className="flex flex-col mt-3 ">
                  <Label label="Discount Amount" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikInput type="number" name="discount_value" rootClassName="w-full" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                <div className="flex flex-col ">
                  <Label label="GST (%)" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikInput name="gst" type="number" rootClassName="w-full" />
                </div>
                <div className="flex flex-col ">
                  <Label label="On Hover Heading" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikInput name="on_hover_heading" rootClassName="w-full" />
                </div>

                <div className="flex flex-col ">
                  <Label label="CTA Button Title" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikInput name="cta_button" rootClassName="w-full" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col col-span-2 mt-2 ">
                  <Label label="Plan Description" className="mb-2 text-black text-[15px] font-semibold" />
                  <FormikTextarea name="description" rootClassName="w-full" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col mt-2 col-span-3">
                  <FieldArray
                    name="plan_features"
                    render={(arrayHelpers) => {
                      return (
                        <>
                          <div className="flex justify-between mb-1 mt-2">
                            <Label label="Plan Features" className="mb-2 text-black text-[15px] font-semibold" />
                            <div
                              className="bg-[#7C13CA] p-1 rounded-full w-auto cursor-pointer mb-2  "
                              onClick={() => {
                                arrayHelpers.push({
                                  title: "",
                                  description: "",
                                  details: "",
                                  icon: "",
                                  desktop_image_url: "",
                                });
                              }}
                            >
                              <Plus className="text-white" />
                            </div>
                          </div>
                          <div className="flex flex-col w-full gap-2">
                            {values?.plan_features?.map((item, index) => (
                              <div className="flex flex-col" key={index}>
                                <div className="flex gap-2">
                                  <Label label={`${index + 1}.`} className="mb-2 pt-3 text-black text-[14px] w-4" />
                                  <div className="flex flex-col w-full">
                                    <FormikInput name={`plan_features.${index}.feature`} rootClassName="w-full" />
                                  </div>
                                  <div className="flex justify-center mt-2 cursor-pointer" onClick={() => arrayHelpers.remove(index)}>
                                    <Trash />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    }}
                  />
                </div>
              </div>

              <div className="fixed bottom-0 z-[99]  left-0 w-full bg-[#fef5e4] hover:text-white flex justify-end items-center py-3 rounded-lg pr-4 space-x-3 shadow-md">
                <Button type="submit" className=" !border-none  text-white rounded-md  !bg-[#7C13CA] ">
                  Save
                </Button>
              </div>
            </ScrollToFormError>
          </Form>
        )}
      </Formik>
    </Section>
  );
}
